import { useEffect, useRef } from 'react';
import {
  shouldFetchDevicesWhenSettingsReady,
  shouldFetchDevicesOnTrackModeChange,
  scheduleDashboardDeviceFetch
} from '../utils/dashboard/deviceFetchScheduleHelpers';
import { dashboardDebug, dashboardWarn } from '../utils/dashboard/dashboardDebug';

/**
 * 设置就绪后的设备列表拉取 / 轨迹模式变化刷新 / 超时兜底 / 自动刷新
 * 说明：就绪拉取只在本 hook 内安排，避免与 settings 生命周期重复请求。
 * 自动刷新必须经 fetchDevicesRef，避免定时器闭包锁死旧的轨迹参数（如默认 recent/60）。
 */
export default function useDashboardDeviceRefreshEffects({
  settingsLoaded,
  trackModeApplied,
  trackMode,
  trackMinutes,
  trackHours,
  trackDays,
  refreshInterval,
  fetchDevices,
  requestManager,
  setTrackModeApplied,
  setSettingsLoaded
}) {
  const readyFetchScheduledRef = useRef(false);
  const skipNextTrackModeFetchRef = useRef(false);
  // 超时兜底须读最新就绪状态，不能闭包在 mount 时的 false
  const settingsLoadedRef = useRef(settingsLoaded);
  const trackModeAppliedRef = useRef(trackModeApplied);
  const fetchDevicesRef = useRef(fetchDevices);
  settingsLoadedRef.current = settingsLoaded;
  trackModeAppliedRef.current = trackModeApplied;
  fetchDevicesRef.current = fetchDevices;

  // 设置 + 轨迹模式就绪 → 拉取（仅安排一次初始就绪拉取）
  useEffect(() => {
    if (!shouldFetchDevicesWhenSettingsReady({ settingsLoaded, trackModeApplied })) {
      if (settingsLoaded && !trackModeApplied) {
        dashboardDebug('用户设置已加载但轨迹模式未应用，等待轨迹模式应用完成...');
      } else if (!settingsLoaded) {
        dashboardDebug('等待用户设置加载完成...');
      }
      return undefined;
    }

    if (readyFetchScheduledRef.current) return undefined;
    readyFetchScheduledRef.current = true;
    skipNextTrackModeFetchRef.current = true;
    dashboardDebug('用户设置和轨迹模式都已加载完成，开始获取设备列表...');
    const timer = scheduleDashboardDeviceFetch(() => fetchDevicesRef.current?.(), 200);
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [settingsLoaded, trackModeApplied]);

  // 轨迹模式参数变化 → 重新拉取（需已水合；跳过与就绪拉取同轮的重复触发）
  useEffect(() => {
    const hydrated = typeof window !== 'undefined' && !!window.dashboardSettingsHydrated;
    if (!shouldFetchDevicesOnTrackModeChange({
      settingsHydrated: hydrated,
      settingsLoaded,
      trackModeApplied
    })) {
      return undefined;
    }

    if (skipNextTrackModeFetchRef.current) {
      skipNextTrackModeFetchRef.current = false;
      return undefined;
    }

    dashboardDebug('轨迹模式设置变化，重新获取设备列表...', {
      trackMode,
      trackMinutes,
      trackHours,
      trackDays
    });
    const timer = scheduleDashboardDeviceFetch(() => fetchDevicesRef.current?.(), 100);
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [trackMode, trackMinutes, trackHours, trackDays, settingsLoaded, trackModeApplied]);

  // 20s 超时兜底：仅当「此刻」仍未就绪才放行（用 ref，避免 [] 闭包永远看到初始 false）
  useEffect(() => {
    const fallbackTimer = setTimeout(() => {
      if (!settingsLoadedRef.current || !trackModeAppliedRef.current) {
        dashboardWarn('设置加载超时，使用当前内存中的轨迹参数继续加载（不写入默认 recent/60）');
        setTrackModeApplied?.(true);
        setSettingsLoaded?.(true);
        scheduleDashboardDeviceFetch(() => fetchDevicesRef.current?.(), 100);
      }
    }, 20000);

    return () => clearTimeout(fallbackTimer);
  }, [setTrackModeApplied, setSettingsLoaded]);

  // 定时自动刷新：始终调用最新 fetchDevices（内部再读最新轨迹参数 ref）
  useEffect(() => {
    if (!settingsLoaded) {
      dashboardDebug('设置尚未加载完成，跳过自动刷新设置');
      return undefined;
    }

    dashboardDebug('设置加载完成，开始自动刷新定时器...', refreshInterval, 'ms');
    const refreshTimer = setInterval(() => {
      dashboardDebug('自动刷新队员状态...');
      fetchDevicesRef.current?.();
    }, refreshInterval);

    return () => {
      dashboardDebug('清理刷新定时器和待处理请求');
      clearInterval(refreshTimer);
      requestManager?.cancelAllRequests?.();
    };
  }, [refreshInterval, settingsLoaded, requestManager]);
}
